import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { Observable, forkJoin, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import farmFlexLossCauseConstants from '../../../../assets/farmFlexLossCauseConstants.json';
import { LoggerService } from '../../../shared/services/logger.service';
import { SearchForClaimsService } from '../services/search-for-claims.service';
import { AggregateLimitsResponse, AggregateLimitsService } from './aggregate-limits.service';
import { PolicySummaryResponse, PolicySummaryService } from './policy-summary.service';

const FARM_FLEX_LOSS_CAUSE_CONSTANTS: { [key: string]: string } = farmFlexLossCauseConstants;

export interface FarmFlexDeductibleClaim {
    amountPaidToFfd: number | null;
    claimNumber: string;
    lossDate: string;
    lossLocation: string;
    status: string;
    typeOfClaim: string;
}

export interface FarmFlexDeductibleClaimsResult {
    claims: FarmFlexDeductibleClaim[];
    deductiblePaid: number | null;
    deductibleTotal: number | null;
    maintenanceDeductible: number | null;
    deductibleThreshold: number | null;
    totalCount: number;
}

interface ClaimLossLocation {
    displayName?: string;
}

interface ClaimSummary {
    amountPaidToFfd?: number | null;
    claimNumber?: string;
    claimState?: string;
    description?: string;
    lossCause?: string;
    lossDate?: string;
    lossLocation?: ClaimLossLocation;
    publicID?: string;
}

interface SearchClaimsResponseBody {
    claimSummaries?: ClaimSummary[];
    totalCount?: number;
}

interface SearchClaimsResponseHeaders {
    get?: (name: string) => string | null;
}

interface SearchClaimsResponse {
    body?: SearchClaimsResponseBody;
    headers?: SearchClaimsResponseHeaders;
}

@Injectable()
export class FarmFlexDeductibleService {
    private currentPolicyNumber = '';

    constructor(
        private searchForClaimsService: SearchForClaimsService,
        private logger: LoggerService,
        private datePipe: DatePipe,
        private policySummaryService: PolicySummaryService,
        private aggregateLimitsService: AggregateLimitsService
    ) {
        this.logger.setModuleName('FarmFlexDeductibleService');
    }

    getAggregateLimits(claimNumber: string, policyPublicID: string, policyNumber?: string): Observable<AggregateLimitsResponse> {
        const resolvedPolicyNumber = policyNumber ?? this.currentPolicyNumber;
        return this.aggregateLimitsService.getAggregateLimits(claimNumber, policyPublicID, resolvedPolicyNumber);
    }

    getPolicySummary(claimNumber: string, policyNumber?: string): Observable<PolicySummaryResponse> {
        const resolvedPolicyNumber = policyNumber ?? this.currentPolicyNumber;
        return this.policySummaryService.getPolicySummary(claimNumber, resolvedPolicyNumber);
    }

    searchClaimsByPolicyNumber(policyNumber: string, offset = 0, limit = 10): Observable<FarmFlexDeductibleClaimsResult> {
        const requestBody = { policyNumber };
        const queryParameters = `?incidentOnly=false&limit=${limit}&offset=${offset}`;
        this.currentPolicyNumber = policyNumber;
        const userId = this.getUserId();

        this.logger.info(`Fetching claims for policy "${policyNumber}" offset=${offset} limit=${limit} - userId: ${userId}, policyNumber: ${policyNumber || 'N/A'}`);

        return this.searchForClaimsService.searchClaims(requestBody, queryParameters).pipe(
            switchMap((response: SearchClaimsResponse) => {
                const claimSummaries: ClaimSummary[] = response?.body?.claimSummaries ?? [];
                const totalCount = this.resolveTotalCount(response, claimSummaries, offset, limit);
                this.logger.debug(`Retrieved ${claimSummaries.length} claims, total=${totalCount} - userId: ${userId}, policyNumber: ${policyNumber || 'N/A'}`);
                const mappedClaims = claimSummaries.map((claim) => this.mapToClaim(claim));
                return this.enrichWithAggregateAmounts(mappedClaims, policyNumber).pipe(
                    map(({ claims, deductiblePaid, deductibleTotal, maintenanceDeductible, deductibleThreshold }) => ({
                        claims,
                        deductiblePaid,
                        deductibleTotal,
                        maintenanceDeductible,
                        deductibleThreshold,
                        totalCount
                    }))
                );
            }),
            catchError((error: unknown) => {
                this.logger.error(`Failed to retrieve claims - userId: ${userId}, policyNumber: ${policyNumber || 'N/A'}`, <object>error);
                throw error;
            })
        );
    }

    private calculateExplicitTotal(response: SearchClaimsResponse): number {
        const headerTotal = Number(response?.headers?.get?.('X-Total-Count'));
        if (headerTotal > 0) {
            return headerTotal;
        }
        const bodyTotal = Number(response?.body?.totalCount);
        return bodyTotal > 0 ? bodyTotal : 0;
    }

    private capitalizeFirstLetter(value: string): string {
        if (!value) {
            return '';
        }
        const lower = value.toLowerCase();
        return lower.charAt(0).toUpperCase() + lower.slice(1);
    }

    private enrichWithAggregateAmounts(
        claims: FarmFlexDeductibleClaim[],
        policyNumber: string
    ): Observable<{ claims: FarmFlexDeductibleClaim[]; deductiblePaid: number | null; deductibleTotal: number | null; maintenanceDeductible: number | null; deductibleThreshold: number | null }> {
        if (claims.length === 0) {
            return of({ claims, deductiblePaid: null, deductibleTotal: null, maintenanceDeductible: null, deductibleThreshold: null });
        }
        const aggregateRequests = claims.map((claim) =>
            this.fetchAggregateAmounts(claim.claimNumber, policyNumber)
        );
        return forkJoin(aggregateRequests).pipe(
            map((amounts) => ({
                claims: claims.map((claim, index) => ({ ...claim, amountPaidToFfd: amounts[index].amountPaidToFfd })),
                deductiblePaid: this.resolveFirstNumericValue(amounts.map((amount) => amount.usedAmount)),
                deductibleTotal: this.resolveDeductibleTotal(amounts),
                maintenanceDeductible: this.resolveMaintenanceDeductible(amounts),
                deductibleThreshold: this.resolveDeductibleThreshold(amounts)
            }))
        );
    }

    private extractAggregateLimit(response: AggregateLimitsResponse): number | null {
        const body = response?.body;
        if (!body) {
            return null;
        }
        const limitItems = body.aggregateLimits ?? [];
        const firstLimitAmount = limitItems.length > 0 ? limitItems[0]?.limitAmount : null;
        return this.resolveFirstNumericValue([
            body.limitAmount,
            firstLimitAmount
        ]);
    }

    private extractDepletionAmount(response: AggregateLimitsResponse): number | null {
        const body = response?.body;
        if (!body) {
            return null;
        }
        const limitItems = body.aggregateLimits ?? [];
        return this.resolveFirstNumericValue([
            body.depletionAmount,
            ...limitItems.map((item) => item?.depletionAmount)
        ]);
    }

    private extractMaintenanceDeductible(response: AggregateLimitsResponse): number | null {
        const body = response?.body;
        if (!body) {
            return null;
        }
        const limitItems = body.aggregateLimits ?? [];
        return this.resolveFirstNumericValue([
            body.maintenanceDedAmt,
            ...limitItems.map((item) => item?.maintenanceDedAmt)
        ]);
    }

    private extractThresholdAmount(response: AggregateLimitsResponse): number | null {
        const body = response?.body;
        if (!body) {
            return null;
        }
        const limitItems = body.aggregateLimits ?? [];
        return this.resolveFirstNumericValue([
            body.thresholdAmount,
            ...limitItems.map((item) => item?.thresholdAmount)
        ]);
    }

    private extractUsedAmount(response: AggregateLimitsResponse): number | null {
        const body = response?.body;
        if (!body) {
            return null;
        }
        const limitItems = body.aggregateLimits ?? [];
        const firstUsedAmount = limitItems.length > 0 ? limitItems[0]?.usedAmount : null;
        return this.resolveFirstNumericValue([
            firstUsedAmount,
            body.usedAmount
        ]);
    }

    private fetchAggregateAmounts(claimNumber: string, policyNumber: string): Observable<{ amountPaidToFfd: number | null; usedAmount: number | null; aggregateLimit: number | null; maintenanceDeductible: number | null; deductibleThreshold: number | null }> {
        if (!claimNumber) {
            return of({ amountPaidToFfd: null, usedAmount: null, aggregateLimit: null, maintenanceDeductible: null, deductibleThreshold: null });
        }
        const userId = this.getUserId();
        return this.getPolicySummary(claimNumber, policyNumber).pipe(
            map((response: PolicySummaryResponse) => this.resolvePolicySummaryPublicID(response)),
            switchMap((policyPublicID: string) => {
                if (!policyPublicID) {
                    this.logger.debug(`Skipping aggregate limits for claim "${claimNumber}" — missing policy public ID - userId: ${userId}, policyNumber: ${policyNumber || 'N/A'}`);
                    return of({ amountPaidToFfd: null, usedAmount: null, aggregateLimit: null, maintenanceDeductible: null, deductibleThreshold: null });
                }

                return this.getAggregateLimits(claimNumber, policyPublicID, policyNumber).pipe(
                    map((response: AggregateLimitsResponse) => ({
                        amountPaidToFfd: this.extractDepletionAmount(response),
                        usedAmount: this.extractUsedAmount(response),
                        aggregateLimit: this.extractAggregateLimit(response),
                        maintenanceDeductible: this.extractMaintenanceDeductible(response),
                        deductibleThreshold: this.extractThresholdAmount(response)
                    })),
                    catchError((error: unknown) => {
                        this.logger.error(`Failed to retrieve aggregate limits for claim "${claimNumber}" - userId: ${userId}, policyNumber: ${policyNumber || 'N/A'}`, <object>error);
                        return throwError(() => error);
                    })
                );
            }),
            catchError((error: unknown) => {
                this.logger.error(`Failed to retrieve policy summary for claim "${claimNumber}" - userId: ${userId}, policyNumber: ${policyNumber || 'N/A'}`, <object>error);
                return throwError(() => error);
            })
        );
    }

    private formatAddressSegment(segment: string, isStateZipSegment: boolean): string {
        const tokens = segment.split(' ');
        return tokens
            .map((token, index) => this.formatLossLocationToken(token, isStateZipSegment && index === 0))
            .join(' ');
    }

    private formatDate(isoDate: string | null | undefined): string {
        if (!isoDate) {
            return '';
        }

        const parsedDate = new Date(isoDate);
        if (isNaN(parsedDate.getTime())) {
            return '';
        }

        return this.datePipe.transform(parsedDate, 'MM/dd/yyyy') ?? '';
    }

    private formatLossLocation(displayName: string): string {
        if (!displayName) {
            return '';
        }
        // Split on commas, format each piece, then join: street on its own line, city + state + ZIP on the next.
        const segments = displayName
            .split(',')
            .map((segment) => segment.trim())
            .filter((segment) => segment.length > 0);
        const lastIndex = segments.length - 1;
        const formattedSegments = segments.map((segment, index) =>
            this.formatAddressSegment(segment, index === lastIndex)
        );
        if (formattedSegments.length <= 1) {
            return formattedSegments.join(' ');
        }
        const cityStateZipSegmentCount = 2;
        const streetPart = formattedSegments.slice(0, -cityStateZipSegmentCount).join(', ');
        const cityStateZipPart = formattedSegments.slice(-cityStateZipSegmentCount).join(', ');
        return streetPart ? `${streetPart}\n${cityStateZipPart}` : cityStateZipPart;
    }

    private formatLossLocationToken(token: string, allowStateAbbreviation: boolean): string {
        if (!token) {
            return token;
        }
        const stateAbbreviationLength = 2;
        if (allowStateAbbreviation && token.length === stateAbbreviationLength && /^[A-Z]{2}$/.test(token)) {
            return token;
        }
        // Leave numeric tokens (e.g. street numbers, ZIP) unchanged.
        if (/^\d/.test(token)) {
            return token;
        }
        return token.charAt(0).toUpperCase() + token.slice(1).toLowerCase();
    }

    private getUserId(): string {
        return window.sessionStorage.getItem('userId') ?? '';
    }

    // Falls back to description if lossCause is missing, empty, or not in the mapping
    private mapLossCause(claim: ClaimSummary): string {
        return FARM_FLEX_LOSS_CAUSE_CONSTANTS[claim.lossCause?.trim() ?? ''] ??
            this.capitalizeFirstLetter(claim.description ?? '');
    }

    private mapToClaim(claim: ClaimSummary): FarmFlexDeductibleClaim {
        return {
            amountPaidToFfd: claim.amountPaidToFfd ?? null,
            claimNumber: claim.claimNumber ?? '',
            lossDate: this.formatDate(claim.lossDate ?? ''),
            lossLocation: this.formatLossLocation(claim.lossLocation?.displayName ?? ''),
            status: this.capitalizeFirstLetter(claim.claimState ?? ''),
            typeOfClaim: this.mapLossCause(claim)
        };
    }

    private parseCurrencyString(value: number | string | null | undefined): number | null {
        if (value === null || value === undefined || value === '') {
            return null;
        }
        if (typeof value === 'number') {
            return Number.isNaN(value) ? null : value;
        }
        const sanitized = value.replace(/[$,\s]/g, '');
        if (sanitized === '') {
            return null;
        }
        const numericValue = Number(sanitized);
        return Number.isNaN(numericValue) ? null : numericValue;
    }

    private resolveDeductibleThreshold(amounts: { deductibleThreshold: number | null }[]): number | null {
        return this.resolveFirstNumericValue(amounts.map((amount) => amount.deductibleThreshold));
    }

    private resolveDeductibleTotal(amounts: { aggregateLimit: number | null }[]): number | null {
        return this.resolveFirstNumericValue(amounts.map((amount) => amount.aggregateLimit));
    }

    private resolveFirstNumericValue(values: (number | string | null | undefined)[]): number | null {
        for (const value of values) {
            const parsed = this.parseCurrencyString(value);
            if (parsed !== null) {
                return parsed;
            }
        }

        return null;
    }

    private resolveFirstStringValue(values: (string | undefined)[]): string {
        const firstString = values.find((value) => typeof value === 'string' && value.length > 0);
        return firstString ?? '';
    }

    private resolveMaintenanceDeductible(amounts: { maintenanceDeductible: number | null }[]): number | null {
        return this.resolveFirstNumericValue(amounts.map((amount) => amount.maintenanceDeductible));
    }

    private resolvePolicyBodyPublicID(policySummaryBody?: { publicID?: string }): string {
        const body = policySummaryBody ?? {};
        return this.resolveFirstStringValue([
            body.publicID
        ]);
    }

    private resolvePolicySummaryPublicID(response: PolicySummaryResponse): string {
        return this.resolvePolicyBodyPublicID(response?.body);
    }

    private resolveTotalCount(response: SearchClaimsResponse, claimSummaries: ClaimSummary[], offset: number, limit: number): number {
        const explicitTotal = this.calculateExplicitTotal(response);
        if (explicitTotal > 0) {
            return explicitTotal;
        }
        // No total provided — if page is full, assume at least one more page exists.
        const extraIfFullPage = claimSummaries.length === limit ? 1 : 0;
        return offset + claimSummaries.length + extraIfFullPage;
    }
}
